// frontend/src/pages/Propostas.js
// Propostas comerciais: números do período na capa, lista na folha com ações
// por status. O documento abre num painel lateral.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Eye, Download, Edit, Send, ArrowRightCircle, XCircle, Trash2, Loader2, MoreHorizontal, FileText, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { API_URL, useAuth, VENDAS } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ConfirmDialog';
import WorkflowStatusFilter from '@/components/WorkflowStatusChips';
import { EtapaFinanciamentoChip, PROPOSTA_STATUS_CONFIG, PropostaChip, statusEfetivo, textoValidade } from '@/components/PropostaStatusChips';
import PropostaDocumentoSheet from '@/components/PropostaDocumentoSheet';
import FinanciamentoDialog, { ACAO_FINANCIAMENTO } from '@/components/FinanciamentoDialog';
import { Card } from '@/components/ui/card';
import { PageCover, PageBody, CoverButton, CoverSearch, SheetHeader, Segmented, PERIODOS, legendaPeriodo } from '@/components/Page';
import { formatBRL, formatBRLCompacto, formatNumero, formatDataHora } from '@/lib/format';
import { FINANCIAMENTO, etapaFinanciamento, proximasEtapas } from '@/lib/pagamento';
import { AVISO_VENDA_SEM_ESTOQUE, faltasDoErro, linhasDeFalta } from '@/lib/estoque';

const ABERTAS = ['rascunho', 'enviada', 'expirada'];
// Proposta de financiamento não passa pelo Converter: vira pedido quando o time de Financiamentos aprova o
// financiamento (o backend recusa o Converter para ela).
const peloConverter = (p) => etapaFinanciamento(p) === null;
// Segundo critério do filtro: a fila de Financiamentos vem do servidor (toda proposta aberta com a forma, de
// qualquer data, da mais antiga para a mais nova).
const FILAS = { [FINANCIAMENTO]: { label: 'Financiamento' } };
// Time de Financiamentos e master: movem a etapa do financiamento (o servidor confere de novo).
const PERFIS_FINANCIAMENTO = ['financiamentos', 'master'];

// Ações da linha (fora do componente para não recriar a cada render). A linha
// inteira abre o documento; os botões param a propagação para não abrir junto.
const parar = (fn) => (e) => { e.stopPropagation(); fn(); };

// Botão só com ícone, com o nome no tooltip.
const AcaoIcone = ({ onClick, icon: Icon, label, disabled = false, testid }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        size="icon" variant="ghost" disabled={disabled} aria-label={label} data-testid={testid}
        onClick={parar(onClick)}
        className="h-8 w-8 text-gray-500 hover:text-foreground"
      >
        <Icon className={`h-4 w-4 ${Icon === Loader2 ? 'animate-spin' : ''}`} />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

const ItemMenu = ({ onClick, icon: Icon, children, danger = false, disabled = false }) => (
  <DropdownMenuItem
    disabled={disabled}
    onSelect={onClick}
    className={danger ? 'text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-400 dark:focus:bg-red-500/10 dark:focus:text-red-300' : ''}
  >
    <Icon className="mr-2 h-4 w-4" />{children}
  </DropdownMenuItem>
);

const Propostas = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [propostas, setPropostas] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState(searchParams.get('busca') || '');
  // Chegando pelo selo do pedido (?busca=), a proposta pode ser antiga — o
  // recorte de 30 dias a esconderia da lista.
  const [periodo, setPeriodo] = useState(searchParams.get('busca') ? 'tudo' : '30d');
  const [statusFilter, setStatusFilter] = useState('all');
  // O time de Financiamentos abre direto na fila dele.
  const [fila, setFila] = useState(user?.role === 'financiamentos' ? FINANCIAMENTO : 'all');
  const [acaoFinanciamento, setAcaoFinanciamento] = useState(null); // { etapa, proposta } do diálogo aberto
  const [docAberto, setDocAberto] = useState(null);
  const [baixandoId, setBaixandoId] = useState(null);
  const [converter, setConverter] = useState(null);
  const [convertendo, setConvertendo] = useState(false);
  const [recusar, setRecusar] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [excluir, setExcluir] = useState(null);
  const [agindo, setAgindo] = useState(false);

  const podeEscrever = ['comercial', 'sales_ops', 'master'].includes(user?.role);
  const podeFinanciar = PERFIS_FINANCIAMENTO.includes(user?.role);
  // O pedido gerado só vira link para quem abre a tela de Pedidos (os demais voltariam ao início).
  const abrePedidos = VENDAS.includes(user?.role);
  const naFila = fila === FINANCIAMENTO;
  // Próximas etapas que o time de Financiamentos pode dar nesta proposta (vazio para os outros perfis).
  const acoesDe = (p) => (podeFinanciar && ABERTAS.includes(statusEfetivo(p)) ? proximasEtapas(p) : []);

  const fetchPropostas = useCallback(async () => {
    try {
      const params = {};
      if (busca.trim()) params.busca = busca.trim();
      // A fila ignora o período: a proposta que espera a financeira há meses continua nela.
      if (fila === FINANCIAMENTO) params.fila_financiamento = 1;
      else if (periodo !== 'tudo') params.periodo = periodo;
      const [lista, k] = await Promise.all([
        axios.get(`${API_URL}/propostas`, { params }),
        axios.get(`${API_URL}/propostas/kpis`, { params: { periodo } }),
      ]);
      setPropostas(lista.data);
      setKpis(k.data);
    } catch {
      toast.error('Erro ao carregar propostas');
    } finally {
      setLoading(false);
    }
  }, [busca, periodo, fila]);

  useEffect(() => {
    const delay = busca ? 350 : 0;
    const t = setTimeout(fetchPropostas, delay);
    return () => clearTimeout(t);
  }, [fetchPropostas, busca]);

  // Chips de status filtram client-side (troca instantânea + contagens).
  const counts = useMemo(() => {
    const c = { all: propostas.length };
    propostas.forEach((p) => { const s = statusEfetivo(p); c[s] = (c[s] || 0) + 1; });
    return c;
  }, [propostas]);
  const visiveis = useMemo(
    () => (statusFilter === 'all' ? propostas : propostas.filter((p) => statusEfetivo(p) === statusFilter)),
    [propostas, statusFilter],
  );

  const baixarPdf = async (p) => {
    setBaixandoId(p.id);
    try {
      const res = await axios.get(`${API_URL}/propostas/${p.id}/pdf`, { responseType: 'blob' });
      const cliente = (p.dados?.cliente_nome || 'cliente').normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^\w]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proposta-${p.numero_proposta}-${cliente}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.status === 503
        ? 'PDF indisponível no momento. Abra Visualizar e use Imprimir ou Salvar como PDF.'
        : 'Erro ao baixar o PDF');
    } finally {
      setBaixandoId(null);
    }
  };

  const mudarStatus = async (p, status, motivoTxt) => {
    setAgindo(true);
    try {
      await axios.patch(`${API_URL}/propostas/${p.id}/status`, { status, motivo: motivoTxt || null });
      toast.success(status === 'enviada' ? `${p.numero_proposta} marcada como enviada` : `${p.numero_proposta} recusada`);
      setRecusar(null);
      setMotivo('');
      fetchPropostas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar a proposta');
    } finally {
      setAgindo(false);
    }
  };

  const confirmarConversao = async () => {
    if (!converter) return;
    setConvertendo(true);
    try {
      // Faltas já mostradas no diálogo e confirmadas: vende sem estoque livre e o pedido sai marcado.
      const params = converter.faltas ? { confirmar_sem_estoque: true } : undefined;
      const { data } = await axios.post(`${API_URL}/propostas/${converter.id}/converter`, null, { params });
      toast.success(`${data.pedido.numero_pedido} criado a partir da ${converter.numero_proposta}`, { duration: 6000 });
      setConverter(null);
      setDocAberto(null);
      navigate('/pedidos');
    } catch (e) {
      const faltas = faltasDoErro(e);
      if (faltas) {
        setConverter({ ...converter, faltas }); // o mesmo diálogo passa a mostrar o que falta e pede confirmação
        return;
      }
      toast.error(e.response?.data?.detail || 'Erro ao converter a proposta');
    } finally {
      setConvertendo(false);
    }
  };

  // Depois de uma ação do financiamento o documento aberto mostra a proposta como o servidor devolveu (null: ela
  // mudou no meio do caminho e o documento fecha) e a lista recarrega.
  const financiamentoConcluido = (nova) => {
    setAcaoFinanciamento(null);
    setDocAberto((d) => (d && nova?.id === d.id ? nova : null));
    fetchPropostas();
  };

  const confirmarExclusao = async () => {
    if (!excluir) return;
    setAgindo(true);
    try {
      await axios.delete(`${API_URL}/propostas/${excluir.id}`);
      toast.success(`${excluir.numero_proposta} excluída`);
      setExcluir(null);
      fetchPropostas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir a proposta');
    } finally {
      setAgindo(false);
    }
  };

  return (
    <div data-testid="propostas-page">
      <PageCover
        title="Propostas"
        subtitle={kpis
          ? `${legendaPeriodo(periodo)} · ${formatNumero(kpis.recusadas_qtd)} ${kpis.recusadas_qtd === 1 ? 'recusada' : 'recusadas'}, ${formatNumero(kpis.expiradas_qtd)} ${kpis.expiradas_qtd === 1 ? 'expirada' : 'expiradas'}`
          : legendaPeriodo(periodo)}
        actions={
          <>
            <CoverSearch value={busca} onChange={setBusca} placeholder="Nº, cliente ou vendedor" data-testid="filtro-busca-proposta" />
            {podeEscrever && (
              <CoverButton primary onClick={() => navigate('/propostas/nova')} data-testid="nova-proposta-button">
                <Plus /> Nova proposta
              </CoverButton>
            )}
          </>
        }
        figures={[
          { value: formatBRLCompacto(kpis?.abertas_valor), label: 'em aberto' },
          { value: formatNumero(kpis?.abertas_qtd), label: kpis?.abertas_qtd === 1 ? 'proposta aberta' : 'propostas abertas' },
          { value: formatNumero(kpis?.convertidas_qtd), label: `viraram pedido, ${formatBRLCompacto(kpis?.convertidas_valor)}` },
          { value: `${kpis?.taxa_conversao ?? 0}%`, label: 'taxa de conversão' },
        ]}
      />
      <PageBody>
        <Card className="overflow-hidden">
          <SheetHeader title={`${naFila ? 'Fila de financiamento' : 'Propostas'} · ${formatNumero(visiveis.length)}`}>
            {/* a fila não tem período: o seletor não teria efeito nela */}
            {!naFila && <Segmented options={PERIODOS} value={periodo} onChange={setPeriodo} label="Período" testid="propostas-periodo" />}
            <WorkflowStatusFilter
              value={statusFilter} onChange={setStatusFilter} counts={counts} config={PROPOSTA_STATUS_CONFIG}
              segundo={{ titulo: 'Fila', opcoes: FILAS, value: fila, onChange: setFila, todos: 'Todas' }}
            />
          </SheetHeader>

          {loading ? (
            <ul aria-hidden className="divide-y divide-border">
              {[1, 2, 3].map((i) => (
                <li key={i} className="grid grid-cols-[minmax(0,1fr)_140px_180px] gap-4 px-5 py-4">
                  <div className="space-y-2">
                    <div className="h-4 w-48 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-72 animate-pulse rounded bg-muted" />
                  </div>
                  <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  <div className="ml-auto h-4 w-24 animate-pulse rounded bg-muted" />
                </li>
              ))}
            </ul>
          ) : visiveis.length === 0 ? (
            <div className="flex flex-col items-center px-5 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-gray-500">
                <FileText className="h-5 w-5" />
              </span>
              <p className="mt-4 text-sm font-medium">Nenhuma proposta encontrada</p>
              <p className="mt-1 max-w-xs text-[13px] text-gray-500">
                {statusFilter !== 'all' || busca ? 'Ajuste o filtro ou a busca acima.'
                  : naFila ? 'Nenhuma proposta de financiamento em aberto.'
                    : podeEscrever ? 'Crie a primeira proposta pelo botão Nova proposta.' : 'Ainda não há propostas no período.'}
              </p>
            </div>
          ) : (
            <TooltipProvider delayDuration={300}>
              {/* Cabeçalho das colunas: só no desktop, onde a grade aparece */}
              <div aria-hidden className="hidden grid-cols-[minmax(0,1fr)_150px_170px_auto] gap-x-5 border-b border-border bg-sheet2 px-5 py-2 text-[11.5px] font-medium uppercase tracking-wide text-gray-500 lg:grid">
                <span>Cliente</span>
                <span>Validade</span>
                <span className="text-right">Total</span>
                <span className="w-[268px]" />
              </div>
              <ul className="divide-y divide-border">
                {visiveis.map((p) => {
                  const st = statusEfetivo(p);
                  const aberta = ABERTAS.includes(st);
                  const validade = textoValidade(p);
                  const baixando = baixandoId === p.id;
                  const itens = p.dados?.itens?.length || 0;
                  const temMenu = podeEscrever && (aberta || ['rascunho', 'recusada'].includes(st));
                  const acoes = acoesDe(p);
                  return (
                    <li
                      key={p.id}
                      data-testid={`proposta-card-${p.numero_proposta}`}
                      onClick={() => setDocAberto(p)}
                      className="group grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-3 px-5 py-3.5 transition-colors duration-150 hover:bg-sheet2 lg:grid-cols-[minmax(0,1fr)_150px_170px_auto] lg:items-center lg:gap-x-5"
                    >
                      {/* Cliente + metadados */}
                      <div className="min-w-0">
                        <p className="truncate text-[15px] font-medium">{p.dados?.cliente_nome || 'Cliente sem nome'}</p>
                        <p className="mt-0.5 truncate text-[12.5px] text-gray-500">
                          <span className="font-medium text-gray-600 dark:text-gray-400">{p.numero_proposta}</span>
                          {' · '}{p.vendedor_nome} · {formatDataHora(p.created_at)} · {itens} {itens === 1 ? 'item' : 'itens'}
                        </p>
                        <EtapaFinanciamentoChip proposta={p} className="mt-1 text-[12.5px]" />
                      </div>

                      {/* Total + status (no celular fica à direita do nome) */}
                      <div className="text-right lg:order-3">
                        <p className="text-[15px] font-medium tabular-nums">{formatBRL(p.totais?.total)}</p>
                        <PropostaChip proposta={p} className="mt-0.5 justify-end text-[12.5px]" />
                      </div>

                      {/* Validade ou pedido gerado */}
                      <div className="col-span-2 text-[13px] lg:order-2 lg:col-span-1">
                        {p.numero_pedido && abrePedidos ? (
                          <button
                            type="button"
                            onClick={parar(() => navigate(`/pedidos?busca=${encodeURIComponent(p.numero_pedido)}`))}
                            className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                          >
                            Pedido {p.numero_pedido}
                          </button>
                        ) : p.numero_pedido ? (
                          <span className="font-medium text-gray-600 dark:text-gray-400">Pedido {p.numero_pedido}</span>
                        ) : validade ? (
                          <span className={st === 'expirada' ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500'}>{validade}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>

                      {/* Ações: ícones + ação principal + menu */}
                      <div className="col-span-2 flex items-center justify-end gap-1 lg:order-4 lg:col-span-1 lg:w-[268px]">
                        <AcaoIcone onClick={() => setDocAberto(p)} icon={Eye} label="Visualizar" testid={`ver-${p.numero_proposta}`} />
                        <AcaoIcone onClick={() => baixarPdf(p)} icon={baixando ? Loader2 : Download} label="Baixar PDF" disabled={baixando} testid={`pdf-${p.numero_proposta}`} />
                        {/* Converter, só quem escreve. A de financiamento vira pedido na aprovação: quem move a etapa tem o
                            menu, os demais veem o rótulo */}
                        {aberta && (peloConverter(p) ? podeEscrever && (
                          <Button size="sm" onClick={parar(() => setConverter(p))} className="ml-1" data-testid={`converter-${p.numero_proposta}`}>
                            <ArrowRightCircle className="mr-1.5 h-4 w-4" />Converter em pedido
                          </Button>
                        ) : acoes.length > 0 ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" onClick={(e) => e.stopPropagation()} className="ml-1" data-testid={`financiamento-menu-${p.numero_proposta}`}>
                                Financiamento<ChevronDown className="ml-1 h-3.5 w-3.5 opacity-70" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
                              {acoes.map((etapa) => (
                                <ItemMenu key={etapa} onClick={() => setAcaoFinanciamento({ etapa, proposta: p })} icon={ACAO_FINANCIAMENTO[etapa].icon} danger={etapa === 'negado'}>
                                  {ACAO_FINANCIAMENTO[etapa].label}
                                </ItemMenu>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span
                            className="ml-1 px-2 text-[12.5px] text-gray-500" data-testid={`financiamento-${p.numero_proposta}`}
                            title="Proposta de financiamento: vira pedido quando o time de Financiamentos aprovar o financiamento."
                          >Financiamento</span>
                        ))}
                        {temMenu && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" aria-label="Mais ações" onClick={(e) => e.stopPropagation()} className="h-8 w-8 text-gray-500 hover:text-foreground" data-testid={`menu-${p.numero_proposta}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
                              {aberta && <ItemMenu onClick={() => navigate(`/propostas/editar/${p.id}`)} icon={Edit}>Editar</ItemMenu>}
                              {st === 'rascunho' && <ItemMenu onClick={() => mudarStatus(p, 'enviada')} icon={Send} disabled={agindo}>Marcar como enviada</ItemMenu>}
                              {aberta && <ItemMenu onClick={() => setRecusar(p)} icon={XCircle} danger>Recusar</ItemMenu>}
                              {['rascunho', 'recusada'].includes(st) && (
                                <>
                                  {aberta && <DropdownMenuSeparator />}
                                  <ItemMenu onClick={() => setExcluir(p)} icon={Trash2} danger>Excluir</ItemMenu>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </TooltipProvider>
          )}
        </Card>
      </PageBody>

      <PropostaDocumentoSheet
        proposta={docAberto} open={Boolean(docAberto)} onOpenChange={(o) => { if (!o) setDocAberto(null); }}
        onBaixarPdf={baixarPdf} baixando={Boolean(docAberto) && baixandoId === docAberto?.id}
        onConverter={(p) => setConverter(p)} podeConverter={podeEscrever && Boolean(docAberto) && ABERTAS.includes(statusEfetivo(docAberto)) && peloConverter(docAberto)}
        acoesFinanciamento={docAberto ? acoesDe(docAberto) : []}
        onFinanciamento={(etapa) => setAcaoFinanciamento({ etapa, proposta: docAberto })}
      />

      {acaoFinanciamento && (
        <FinanciamentoDialog
          key={`${acaoFinanciamento.proposta.id}-${acaoFinanciamento.etapa}`}
          acao={acaoFinanciamento} onFechar={() => setAcaoFinanciamento(null)} onConcluido={financiamentoConcluido}
        />
      )}

      <ConfirmDialog
        open={Boolean(converter)} onOpenChange={(o) => { if (!o) setConverter(null); }}
        variant={converter?.faltas ? 'warning' : 'info'} loading={convertendo} onConfirm={confirmarConversao}
        title={converter?.faltas ? 'Converter sem estoque livre?' : 'Converter em pedido?'}
        confirmText={converter?.faltas ? 'Converter mesmo assim' : 'Converter'}
        description={converter?.faltas ? AVISO_VENDA_SEM_ESTOQUE : `Isso cria o pedido com os dados e preços da ${converter?.numero_proposta || ''}.`}
        details={converter ? [{ label: 'Cliente', value: converter.dados?.cliente_nome }, { label: 'Total', value: formatBRL(converter.totais?.total) }] : undefined}
        warningList={converter ? [
          ...(converter.faltas ? linhasDeFalta(converter.faltas) : []),
          ...(converter.expirada ? ['Esta proposta já passou da validade. Confira se preços e frete ainda valem.'] : []),
        ] : undefined}
      />

      <ConfirmDialog
        open={Boolean(excluir)} onOpenChange={(o) => { if (!o) setExcluir(null); }}
        variant="destructive" title="Excluir proposta?" confirmText="Excluir" loading={agindo} onConfirm={confirmarExclusao}
        description={`A ${excluir?.numero_proposta || ''} será removida. Esta ação não pode ser desfeita.`}
      />

      <Dialog open={Boolean(recusar)} onOpenChange={(o) => { if (!o) { setRecusar(null); setMotivo(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recusar {recusar?.numero_proposta}</DialogTitle>
            <DialogDescription>Registre o motivo, se quiser. Ajuda a entender o que perdeu a venda.</DialogDescription>
          </DialogHeader>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex: preço acima do concorrente, cliente adiou a compra..." rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRecusar(null); setMotivo(''); }} disabled={agindo}>Cancelar</Button>
            <Button variant="destructive" onClick={() => mudarStatus(recusar, 'recusada', motivo)} disabled={agindo}>
              {agindo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Recusar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Propostas;
